import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Download, Info, Loader2 } from 'lucide-react';
import { AnalyticsModal } from '@repo/ui/components/Modal/AnalyticsModal';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { ScopeUnitDto } from '@repo/shared/dtos/analytics/ScopeFilterDto';
import { useSecurityDomain } from '../../hooks/auth';
import { useExportScopeUnits } from '../../hooks/analytics/useExport';
import { exportToCSV } from '../../utils/export/core';
import { ExportFormat } from '../../utils/export/types';
import UnifiedFilterDropdown from './filters/core/UnifiedFilterDropdown';
import { FILTER_CONFIGS } from './filters/constants/filterConfig';
import { useFilterUnits } from './filters/hooks/useFilterUnits';
import {
  EXPORT_DATASETS,
  ExportDatasetKey,
  buildExportFileName,
  fetchExportRows,
} from './export/exportDatasets';
import { MonthRange, normalizeRange } from './export/exportRange';
import ExportDatasetPicker from './export/ExportDatasetPicker';
import ExportMonthRange from './export/ExportMonthRange';

interface AnalyticsExportPanelProps {
  isOpen: boolean;
  onClose: () => void;
  filters: BaseAnalyticsFilter;
  className?: string;
  onExportError?: (error: Error) => void;
}

type ExportStatus = 'idle' | 'loading' | 'empty' | 'error';

const unitsConfig = FILTER_CONFIGS.units;

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-medium uppercase tracking-wide text-[var(--anl-text-muted)]">
        {title}
      </h3>
      {children}
    </section>
  );
}

export default function AnalyticsExportPanel({
  isOpen,
  onClose,
  filters,
  className = '',
  onExportError,
}: AnalyticsExportPanelProps) {
  const queryClient = useQueryClient();
  const { isAllowed: canExportReports } = useSecurityDomain({
    routeObject: 'analytics',
    routeItem: 'export',
    action: 'read',
  });
  const { isAllowed: canSelectUnits } = useSecurityDomain({
    routeObject: 'analytics',
    routeItem: 'scopefilter',
    action: 'read',
  });
  const permissionsReady = canExportReports !== undefined && canSelectUnits !== undefined;

  // Same unit list as the filter bar. Roles without the scope filter (UnitAdmin) can't load
  // it, so their unit comes from the export scope instead.
  const filterUnitsQuery = useFilterUnits(isOpen && canSelectUnits === true);
  const scopeUnitsQuery = useExportScopeUnits(
    isOpen && canSelectUnits === false && canExportReports === true,
  );
  const unitsQuery = canSelectUnits ? filterUnitsQuery : scopeUnitsQuery;
  const units = useMemo<ScopeUnitDto[]>(() => unitsQuery.data ?? [], [unitsQuery.data]);
  const singleUnit = units.length === 1;
  const showUnitPicker = canSelectUnits === true && !singleUnit;

  const [dataset, setDataset] = useState<ExportDatasetKey>('sessionTotals');
  const [range, setRange] = useState<MonthRange | null>(null);
  const [rangeTouched, setRangeTouched] = useState(false);
  const [chosenUnitIds, setChosenUnitIds] = useState<number[]>([]);
  const [status, setStatus] = useState<ExportStatus>('idle');

  const datasets = useMemo(
    () =>
      EXPORT_DATASETS.filter(
        d =>
          (d.group === 'totals' || canExportReports) && !(singleUnit && d.key === 'sessionTotals'),
      ),
    [canExportReports, singleUnit],
  );
  const activeDataset =
    datasets.find(d => d.key === dataset) ??
    datasets.find(d => d.group === 'totals') ??
    datasets[0];
  const maxMonths = activeDataset.maxMonths;
  const isExporting = status === 'loading';

  // Seed from the page filters each time the panel opens; later page changes don't override
  // what the user picked here.
  useEffect(() => {
    if (!isOpen) return;
    setRange(null);
    setRangeTouched(false);
    setChosenUnitIds(filters.unitIds ?? []);
    setStatus('idle');
  }, [isOpen]);

  // Fit the range to the selected dataset's limit. Until the user picks months, derive it from
  // the page range so moving between daily and monthly doesn't keep a shrunken window.
  useEffect(() => {
    if (!isOpen) return;
    setRange(prev =>
      normalizeRange(
        rangeTouched && prev ? prev : { startDate: filters.startDate, endDate: filters.endDate },
        maxMonths,
      ),
    );
  }, [isOpen, maxMonths]);

  useEffect(() => {
    if (!isOpen || isExporting) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, isExporting, onClose]);

  const withReset =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setStatus('idle');
    };

  const listedUnitIds = units.map(u => Number(u.unitid));
  const resolveRequestUnitIds = (): number[] => {
    // Without the scope filter the API limits the export to the caller's own units.
    if (!canSelectUnits) return [];
    // If the list failed to load, send the choice as-is; the API still enforces the scope.
    if (!unitsQuery.isSuccess) return chosenUnitIds;
    const chosen = chosenUnitIds.filter(id => listedUnitIds.includes(id));
    // "All units" sends the listed ids so the file matches the list the user sees.
    return chosen.length > 0 ? chosen : listedUnitIds;
  };

  const canDownload =
    permissionsReady && !!range && !isExporting && !(canSelectUnits && unitsQuery.isLoading);
  const fileName = range ? buildExportFileName(activeDataset.key, range) : '';

  const handleDownload = async () => {
    if (!range) return;
    setStatus('loading');
    try {
      const rows = await fetchExportRows(queryClient, {
        dataset: activeDataset.key,
        startDate: range.startDate,
        endDate: range.endDate,
        unitIds: resolveRequestUnitIds(),
      });
      if (rows.length === 0) {
        setStatus('empty');
        return;
      }
      exportToCSV(rows, { fileName, format: ExportFormat.CSV, date: false });
      setStatus('idle');
      onClose();
    } catch (error) {
      setStatus('error');
      onExportError?.(error instanceof Error ? error : new Error('Export failed'));
    }
  };

  const title = singleUnit ? (
    <>
      Export data{' '}
      <span className="font-normal text-[var(--anl-text-muted)]">
        · {units[0].unitdescription || `Unit ${units[0].unitid}`}
      </span>
    </>
  ) : (
    'Export data'
  );

  const UnitsIcon = unitsConfig.icon;

  return (
    <AnalyticsModal
      isOpen={isOpen}
      onClose={isExporting ? () => undefined : onClose}
      title={title}
      className={`max-w-lg ${className}`}
    >
      <div className="space-y-6 p-1">
        <Section title="What to export">
          <ExportDatasetPicker
            datasets={datasets}
            value={activeDataset.key}
            onChange={withReset(setDataset)}
            singleUnit={singleUnit}
            disabled={isExporting}
          />
        </Section>

        {range && (
          <Section title="Months">
            <ExportMonthRange
              range={range}
              maxMonths={maxMonths}
              onChange={withReset((next: MonthRange) => {
                setRange(next);
                setRangeTouched(true);
              })}
              disabled={isExporting}
            />
          </Section>
        )}

        {showUnitPicker && (
          <Section title="Units">
            <UnifiedFilterDropdown<ScopeUnitDto>
              title={
                chosenUnitIds.length > 0
                  ? unitsConfig.title
                  : `All units${unitsQuery.isSuccess ? ` (${units.length})` : ''}`
              }
              icon={<UnitsIcon size={16} />}
              selectedIds={chosenUnitIds}
              onSelectionChange={withReset(setChosenUnitIds)}
              endpoint={unitsConfig.endpoint}
              getId={unitsConfig.getId}
              getDisplayName={unitsConfig.getDisplayName}
              variant={unitsConfig.variant}
              panelPlacement="viewport"
            />
          </Section>
        )}

        {status === 'empty' && (
          <p
            role="status"
            className="flex items-center gap-2 rounded-md bg-[var(--anl-surface-elev)] px-3 py-2 text-sm text-[var(--anl-text)]"
          >
            <Info className="h-4 w-4 shrink-0 text-[var(--anl-text-muted)]" aria-hidden />
            No data for this selection. Try other months or units.
          </p>
        )}
        {status === 'error' && (
          <p
            role="alert"
            className="flex items-center gap-2 rounded-md bg-[var(--anl-surface-elev)] px-3 py-2 text-sm text-[var(--anl-text)]"
          >
            <AlertCircle className="h-4 w-4 shrink-0 text-[var(--anl-warning)]" aria-hidden />
            <span className="flex-1">The file couldn't be prepared.</span>
            <button
              type="button"
              onClick={handleDownload}
              className="font-medium underline underline-offset-2 hover:opacity-80"
            >
              Retry
            </button>
          </p>
        )}

        <div className="flex items-center justify-between gap-4 border-t border-[var(--anl-border)] pt-4">
          <span
            className="min-w-0 truncate text-xs text-[var(--anl-text-muted)]"
            title={fileName ? `${fileName}.csv` : undefined}
          >
            {fileName && `${fileName}.csv`}
          </span>
          <div className="flex shrink-0">
            <button
              type="button"
              onClick={handleDownload}
              disabled={!canDownload}
              className="flex items-center gap-2 rounded-md bg-[var(--anl-accent)] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--anl-text)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Download className="h-4 w-4" aria-hidden />
              )}
              {isExporting ? 'Preparing…' : 'Download CSV'}
            </button>
          </div>
        </div>
      </div>
    </AnalyticsModal>
  );
}
